import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { NotificationCategory } from "../../../enums/notificationCategories.enum";
import {
  MarkAllNotificationsReadResponse,
  MarkAllNotificationsReadResponseDto,
  Notification,
  NotificationDto,
} from "../../../models/api/notification/notification";
import {
  parseMarkAllNotificationsReadResponse,
  parseRestNotification,
} from "../../../models/api/notification/notification.parser";
import { CursorPagination, CursorPaginationDto } from "../../../models/api/pagination";
import {
  NotificationChannelSetting,
  parseNotificationChannelSettings,
} from "../../../models/api/notification/notification-channel-settings";

export class ParseNotificationError extends Error {
  constructor(message: string) {
    super(message);
  }
}

@Injectable({
  providedIn: "root",
})
export class NotificationsApiService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Fetches all notifications categories.
   * @returns Observable of NotificationCategory[]
   */
  public getNotificationCategories(): Observable<NotificationCategory[]> {
    return this.http
      .get<NotificationCategory[]>(`${environment.baseUrlV2}/notification/categories`)
      .pipe(this.apiErrorHandler.handle("notification"));
  }

  /**
   * Marks all notifications as read.
   * @returns Observable of the HTTP response.
   */
  public readAllNotifications(): Observable<MarkAllNotificationsReadResponse> {
    return this.http
      .put<MarkAllNotificationsReadResponseDto>(`${environment.baseUrlV2}/notification/read`, {})
      .pipe(map(parseMarkAllNotificationsReadResponse), this.apiErrorHandler.handle("notification"));
  }

  /**
   * Marks a specific notification as read.
   * @param notificationId - The ID of the notification to mark as read.
   * @returns Observable of the HTTP response.
   */
  public readSpecificNotification(notificationId: string): Observable<Notification> {
    return this.http
      .put<NotificationDto>(`${environment.baseUrlV2}/notification/${notificationId}/read`, {})
      .pipe(map(parseRestNotification), this.apiErrorHandler.handle("notification"));
  }

  /** Retrieves the delivery settings for notification categories the current role may configure. */
  public getNotificationSettings(): Observable<NotificationChannelSetting[]> {
    return this.http
      .get<unknown>(`${environment.baseUrlV2}/notification/settings`)
      .pipe(map(parseNotificationChannelSettings), this.apiErrorHandler.handle("notification"));
  }

  /** Stores delivery channels for the current user's permitted notification categories. */
  public updateNotificationSettings(settings: NotificationChannelSetting[]): Observable<NotificationChannelSetting[]> {
    return this.http.put<unknown>(`${environment.baseUrlV2}/notification/settings`, { settings }).pipe(
      map(parseNotificationChannelSettings),
      catchError((error) => this.handleNotificationSettingsUpdateError(error))
    );
  }

  /**
   * Fetches notifications with pagination and filters.
   * @param onlyUnread Filter for only unread or all Notifications
   * @param notificationCategory Filter for Notifications of a specific category
   * @param pagination Define how many Notifications you want to see and the starting point
   * @returns An observable that emits an array of Notifications.
   */
  public getNotifications(
    onlyUnread?: boolean,
    notificationCategory?: NotificationCategory,
    pagination?: CursorPagination
  ): Observable<CursorPaginationDto<Notification, { totalUnreadCount: number }>> {
    let params = new HttpParams();

    if (onlyUnread !== undefined) params = params.set("unreadOnly", onlyUnread);
    if (notificationCategory) params = params.set("category", notificationCategory);
    if (pagination?.cursor) params = params.set("pagination[cursor]", pagination.cursor);
    params = params
      .set("pagination[pageSize]", pagination?.pageSize ?? 1)
      .set("sort[column]", "createdAt")
      .set("sort[descending]", true);
    return this.http
      .get<
        CursorPaginationDto<NotificationDto, { totalUnreadCount: number }>
      >(`${environment.baseUrlV2}/notification`, { params })
      .pipe(
        map((response) => ({ ...response, items: response.items.map(parseRestNotification) })),
        this.apiErrorHandler.handle("notification")
      );
  }

  private handleNotificationSettingsUpdateError(error: unknown): Observable<never> {
    const expectedSnapshotChange =
      error instanceof HttpErrorResponse &&
      (error.status === 403 ||
        (error.status === 400 &&
          ["INCOMPLETE_NOTIFICATION_SETTINGS", "DUPLICATE_NOTIFICATION_SETTINGS"].includes(error.error?.code)));

    return expectedSnapshotChange
      ? throwError(() => ({ expectedSnapshotChange }))
      : throwError(() => error).pipe(this.apiErrorHandler.handle("notification"));
  }
}
